import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { ArrowRight } from "lucide-react";

/**
 * Admin-driven homepage bottom CTA section.
 *
 * Layout: text + media side-by-side on desktop (admin choice of which side
 * the text sits on); stacked on mobile. Reuses the same media-type switch
 * as `HeroMedia` so admins get a consistent mental model.
 *
 * Hidden when `enabled === false` so the admin can quietly remove the section
 * without code changes.
 */
const FALLBACK_IMAGE =
  "https://images.pexels.com/photos/3823488/pexels-photo-3823488.jpeg";

export default function BottomCta() {
  const [cta, setCta] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/cta");
        if (!cancelled) setCta(data);
      } catch {
        if (!cancelled) setCta({ enabled: false });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!cta || cta.enabled === false) return null;

  const textBlock = (
    <div className="space-y-5">
      {cta.badge_label ? (
        <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-700 font-semibold">
          {cta.badge_label}
        </div>
      ) : null}
      {cta.headline ? (
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-emerald-950 leading-tight tracking-tight">
          {cta.headline}
        </h2>
      ) : null}
      {cta.subheadline ? (
        <p className="text-base sm:text-lg text-emerald-900/80 leading-relaxed max-w-xl">
          {cta.subheadline}
        </p>
      ) : null}
      <div className="flex flex-wrap gap-3 pt-1">
        {cta.primary_btn_text ? (
          <CtaLink
            to={cta.primary_btn_link || "/register"}
            variant="primary"
            data-testid="cta-primary-btn"
          >
            {cta.primary_btn_text}
            <ArrowRight className="w-4 h-4 ml-1" />
          </CtaLink>
        ) : null}
        {cta.secondary_btn_text ? (
          <CtaLink
            to={cta.secondary_btn_link || "/learn"}
            variant="secondary"
            data-testid="cta-secondary-btn"
          >
            {cta.secondary_btn_text}
          </CtaLink>
        ) : null}
      </div>
      {cta.trust_text ? (
        <p className="text-xs text-emerald-900/60 leading-relaxed pt-1">
          {cta.trust_text}
        </p>
      ) : null}
    </div>
  );

  const mediaBlock = (
    <div className="relative w-full">
      <div className="absolute -inset-4 bg-gradient-to-br from-emerald-100/40 via-white to-amber-100/40 rounded-[2rem] blur-xl" />
      <div className="relative rounded-[1.5rem] overflow-hidden border border-emerald-100 shadow-lg bg-white">
        <CtaMedia cta={cta} />
      </div>
    </div>
  );

  const mediaFirst = cta.layout === "text-right";

  return (
    <section
      className="bg-gradient-to-br from-emerald-50/40 via-white to-amber-50/30 border-y border-emerald-100"
      data-testid="bottom-cta"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 grid md:grid-cols-2 gap-10 lg:gap-16 items-center">
        {mediaFirst ? (
          <>
            {mediaBlock}
            {textBlock}
          </>
        ) : (
          <>
            {textBlock}
            {mediaBlock}
          </>
        )}
      </div>
    </section>
  );
}

/* ---- helpers ---- */

function CtaLink({ to, children, variant, ...rest }) {
  const cls =
    variant === "primary"
      ? "inline-flex items-center justify-center rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-7 py-3.5 text-base font-semibold transition-colors"
      : "inline-flex items-center justify-center rounded-full bg-white border border-emerald-200 text-emerald-800 hover:bg-emerald-50 px-7 py-3.5 text-base font-semibold transition-colors";
  const isExternal = to && /^https?:\/\//i.test(to);
  if (isExternal) {
    return (
      <a href={to} className={cls} target="_blank" rel="noopener noreferrer" {...rest}>
        {children}
      </a>
    );
  }
  return (
    <Link to={to} className={cls} {...rest}>
      {children}
    </Link>
  );
}

function CtaMedia({ cta }) {
  const ref = useRef(null);

  // Pause uploaded videos when out of view to save battery
  useEffect(() => {
    const v = ref.current;
    if (!v || cta.media_type !== "video_upload") return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && cta.autoplay) {
            v.play().catch(() => {});
          } else {
            v.pause();
          }
        });
      },
      { threshold: 0.25 }
    );
    io.observe(v);
    return () => io.disconnect();
  }, [cta.media_type, cta.autoplay]);

  if (cta.media_type === "video_upload" && cta.video_url) {
    return (
      <video
        ref={ref}
        src={cta.video_url}
        poster={cta.poster_url || undefined}
        autoPlay={cta.autoplay}
        muted={cta.muted}
        loop={cta.loop}
        controls={cta.show_controls}
        playsInline
        preload="metadata"
        className="w-full aspect-video object-cover bg-emerald-950"
        data-testid="cta-media-video"
      />
    );
  }
  if (cta.media_type === "youtube" && cta.youtube_embed_url) {
    return (
      <div className="relative w-full aspect-video bg-emerald-950">
        <iframe
          src={cta.youtube_embed_url}
          title="Feel Greats"
          loading="lazy"
          allow="autoplay; encrypted-media; picture-in-picture"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
          className="absolute inset-0 w-full h-full"
          data-testid="cta-media-iframe"
        />
      </div>
    );
  }
  if (cta.media_type === "vimeo" && cta.vimeo_embed_url) {
    return (
      <div className="relative w-full aspect-video bg-emerald-950">
        <iframe
          src={cta.vimeo_embed_url}
          title="Feel Greats"
          loading="lazy"
          allow="autoplay; encrypted-media; picture-in-picture"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
          className="absolute inset-0 w-full h-full"
          data-testid="cta-media-iframe"
        />
      </div>
    );
  }
  return (
    <img
      src={cta.image_url || FALLBACK_IMAGE}
      alt={cta.headline || "Feel Greats"}
      loading="lazy"
      className="w-full aspect-video object-cover"
      data-testid="cta-media-image"
    />
  );
}
